import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { LineChart as line, LineSeriesOption } from 'echarts/charts';
import {
  GridComponent,
  GridComponentOption,
  VisualMapComponent,
  VisualMapComponentOption,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { ComposeOption } from 'echarts/core';

// Регистрируем необходимые компоненты
echarts.use([line, GridComponent, VisualMapComponent, CanvasRenderer]);

// Определяем композитный тип для опций
type ECOption = ComposeOption<
  LineSeriesOption | GridComponentOption | VisualMapComponentOption
>;

type LineChartProps = {
  type: string;
  data: {
    score: number;
    type: string;
    creationDate: string;
  }[];
  levels: {
    [score: number]: {
      title: string;
      color: string;
    };
  };
};

const LineChart: React.FC<LineChartProps> = ({ type, data, levels }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  const minimum: number = type === 'anxiety' ? 20 : 0;

  const levelKeys: number[] = Object.keys(levels).map(Number);
  const maximum: number = Math.max(...levelKeys);

  useEffect(() => {
    if (!chartRef.current) return;

    const myChart = echarts.init(chartRef.current);

    // Отключаем интерактивность
    myChart.setOption({
      silent: true, // Отключает все события мыши
      hoverLayerThreshold: Infinity, // Отключает подсветку при наведении
    });

    // Фильтруем данные по типу, соответствующему id
    const filteredData = data.filter(item => item.type === type);

    // Сортируем данные по дате
    const sortedData = [...filteredData].sort(
      (a, b) =>
        new Date(a.creationDate).getTime() - new Date(b.creationDate).getTime()
    );

    // Определяем фактический диапазон данных с отступами
    let minDataValue = Math.min(...sortedData.map(item => item.score));
    let maxDataValue = Math.max(...sortedData.map(item => item.score));

    // Добавляем отступы в 5 пунктов с каждой стороны
    minDataValue = Math.max(minimum, minDataValue - 2);
    maxDataValue = Math.min(maximum, maxDataValue + 2);

    // Если данных мало или они в узком диапазоне, устанавливаем минимальную высоту шкалы
    if (maxDataValue - minDataValue < 10) {
      const middleValue = (maxDataValue + minDataValue) / 2;
      minDataValue = Math.max(minimum, middleValue - 2);
      maxDataValue = Math.min(maximum, middleValue + 2);
    }

    // Округляем min и max для более читаемых значений на оси
    minDataValue = Math.floor(minDataValue);
    maxDataValue = Math.ceil(maxDataValue);

    // Вычисляем интервал для ровно 5 значений (включая min и max)
    const interval = (maxDataValue - minDataValue) / 4;

    // Сортируем уровни
    const sortedLevels = levelKeys.sort((a, b) => a - b);

    // Создаем цветовую схему для visualMap
    const pieces: Array<{
      gt?: number;
      gte?: number;
      lte?: number;
      color: string;
    }> = [];

    // Проходим по всем уровням и создаем правильные диапазоны
    for (let i = 0; i < sortedLevels.length; i++) {
      const currentLevel = sortedLevels[i];
      const nextLevelIndex = i + 1;

      if (i === 0) {
        // Первый диапазон: от minimum до первого уровня
        pieces.push({
          gte: minimum,
          lte: currentLevel,
          color: levels[currentLevel].color,
        });
      }

      if (nextLevelIndex < sortedLevels.length) {
        // Промежуточные диапазоны
        const nextLevel = sortedLevels[nextLevelIndex];
        pieces.push({
          gt: currentLevel,
          lte: nextLevel,
          color: levels[nextLevel].color,
        });
      } else {
        // Последний диапазон: от последнего уровня до maximum
        pieces.push({
          gt: currentLevel,
          lte: maximum,
          color: levels[currentLevel].color,
        });
      }
    }

    // Удаляем дубликаты диапазонов
    const filteredPieces = pieces.filter(
      (piece, index, self) =>
        index ===
        self.findIndex(
          p =>
            (p.gt === piece.gt && p.lte === piece.lte) ||
            (p.gte === piece.gte && p.lte === piece.lte)
        )
    );

    // Форматируем даты для оси X
    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
    };

    // Создаем массив из ровно 5 значений для оси Y
    const yAxisData = [];
    for (let i = 0; i < 5; i++) {
      yAxisData.push(minDataValue + interval * i);
    }

    const option: ECOption = {
      animation: false, // Отключаем анимацию
      tooltip: {
        show: false, // Отключаем подсказки при наведении
      },
      grid: {
        left: '0',
        right: '0',
        top: '5%',
        bottom: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: sortedData.map(item => formatDate(item.creationDate)),
        axisLabel: {
          show: false, // Скрываем метки оси X
        },
        axisLine: {
          show: false, // Скрываем линию оси X
        },
        axisTick: {
          show: false, // Скрываем деления оси X
        },
      },
      yAxis: {
        type: 'value',
        min: minDataValue,
        max: maxDataValue,
        interval: interval, // Устанавливаем точный интервал между значениями
        axisLine: {
          show: false, // Скрываем линию оси Y
        },
        axisTick: {
          show: false, // Скрываем деления оси Y
        },
        splitLine: {
          show: true, // Показываем горизонтальные линии сетки
          lineStyle: {
            type: 'dashed',
            color: 'rgba(255, 255, 255, 0.3)', // Полупрозрачный белый цвет
          },
        },
      },
      visualMap: {
        show: false,
        dimension: 1, // Указываем, что градиент зависит от значения Y (значение score)
        min: minimum,
        max: maximum, // Для visualMap используем полный диапазон, чтобы цвета были корректными
        seriesIndex: 0,
        pieces: filteredPieces,
        outOfRange: {
          color: '#999',
        },
      },
      series: {
        name: type,
        type: 'line',
        data: sortedData.map(item => item.score),
        lineStyle: {
          width: 3,
        },
        symbol: 'circle',
        symbolSize: 10, // Увеличиваем размер точек
        itemStyle: {
          color: function (params: any) {
            const value = params.value;

            // Проходим по уровням от большего к меньшему
            for (let i = sortedLevels.length - 1; i >= 0; i--) {
              const currentLevel = sortedLevels[i];

              if (value >= currentLevel) {
                return levels[currentLevel].color;
              }
            }

            // Если значение меньше минимального уровня,
            // используем цвет самого низкого уровня
            return levels[sortedLevels[0]].color;
          },
        },
        connectNulls: true, // Соединяем линию, даже если есть пропущенные точки
        smooth: true, // Делаем линию плавной
        silent: true, // Отключаем события на элементах серии
      },
    };

    myChart.setOption(option);

    const resizeHandler = () => {
      myChart.resize();
    };
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      myChart.dispose();
    };
  }, [type, data, levels, minimum, maximum, levelKeys]);

  return (
    <div ref={chartRef} style={{ height: '140px', pointerEvents: 'none' }} />
  );
};

export default LineChart;
